import React from 'react';
import { GenericSearchModal } from './GenericSearchModal';
import { Tr, Td } from '../../infrastructure/interface/tables/Table';
import { useInternationalisation } from '../../internationalisation/hooks/useInternationalisation';
import { MinimalButton } from '../../infrastructure/interface/buttons/MinimalButton';
import { ItemDetailsCard } from '../../infrastructure/interface/components/ItemDetailsCard';
import { FieldAndValue } from '../../infrastructure/interface/components/FieldAndValue';
import styled from 'styled-components/macro';
import { spacing16 } from '../../styling/design/spacing';

type CounterpartIdValue = number;

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  onSelectCounterpartId: (counterpartId: CounterpartIdValue) => void;
};

export const InvestorSearchModal = ({ isOpen, onRequestClose, onSelectCounterpartId }: Props) => {
  const { translate } = useInternationalisation();

  return (
    <GenericSearchModal<GetInvestorSearchResultsQuery, InvestorSearchResult>
      title={translate('searchModals.investor.title')}
      inputPlaceholder={translate('searchModals.investor.placeholder')}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      dataEndpointUrl="/api/investors/GetInvestorSearchResults"
      getQueryParameters={(inputs) => inputs}
      desktopHeaders={[
        translate('searchModals.investor.headings.investor'),
        translate('searchModals.investor.headings.code'),
        translate('searchModals.investor.headings.investorNumber'),
        translate('searchModals.investor.headings.beneficialOwners'),
      ]}
      renderDesktopRow={(result) => (
        <DesktopRow
          key={result.counterpartId}
          result={result}
          onSelectCounterpartId={onSelectCounterpartId}
        />
      )}
      renderMobileItem={(result) => (
        <MobileItemCard
          key={result.counterpartId}
          result={result}
          onSelectCounterpartId={onSelectCounterpartId}
        />
      )}
    />
  );
};

type ResultProps = {
  result: InvestorSearchResult;
  onSelectCounterpartId: (counterpartId: number) => void;
};

const DesktopRow = ({ result, onSelectCounterpartId }: ResultProps) => (
  <Tr>
    <Td>
      <MinimalButton onClick={() => onSelectCounterpartId(result.counterpartId)} textAlign={'left'}>
        {result.investorName}
      </MinimalButton>
    </Td>
    <Td>{result.investorCode}</Td>
    <Td>{result.investorNumber}</Td>
    <Td dangerouslySetInnerHTML={{ __html: result.beneficialOwnersHtml ?? '' }} />
  </Tr>
);

const MobileItemCard = ({ result, onSelectCounterpartId }: ResultProps) => {
  const { translate } = useInternationalisation();

  return (
    <ItemDetailsCard>
      <MobileInvestorNameButton
        onClick={() => onSelectCounterpartId(result.counterpartId)}
        size="large"
        textAlign="left"
      >
        {result.investorName}
      </MobileInvestorNameButton>
      <FieldAndValue
        fieldLabel={translate('searchModals.investor.headings.code')}
        type="text"
        value={result.investorCode}
      />
      <FieldAndValue
        fieldLabel={translate('searchModals.investor.headings.investorNumber')}
        type="text"
        value={result.investorNumber}
      />
      <FieldAndValue
        fieldLabel={translate('searchModals.investor.headings.beneficialOwners')}
        type="custom"
        value={<div dangerouslySetInnerHTML={{ __html: result.beneficialOwnersHtml ?? '' }} />}
      />
    </ItemDetailsCard>
  );
};

const MobileInvestorNameButton = styled(MinimalButton)`
  margin-bottom: ${spacing16};
`;

type GetInvestorSearchResultsQuery = {
  searchTerm: string | null;
  pageNumber: number;
  resultsPerPage: number;
};

type InvestorSearchResult = {
  counterpartId: number;
  investorName: string | null;
  investorCode: string | null;
  investorNumber: string | null;
  beneficialOwnersHtml: string | null;
};
